import { Link } from 'react-router-dom'

function Footer({ variant = 'light' }) {
  const isDark = variant === 'dark'

  return (
    <footer
      className={`${
        isDark
          ? 'bg-charcoal text-white'
          : 'bg-cream text-gray-700'
      } transition-colors duration-300`}
    >
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
          {/* Logo and Tagline */}
          <div className="md:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 group mb-4">
              <img
                src="/images/logo/bakenook-logo.jpeg"
                alt="The BakeNook Logo"
                className="h-10 w-auto object-contain transform transition-transform duration-500 group-hover:scale-110"
              />
              <span
                className={`text-xl font-heading font-bold ${
                  isDark ? 'text-white' : 'text-charcoal'
                }`}
              >
                The BakeNook
              </span>
            </Link>
            <p
              className={`text-sm leading-relaxed max-w-sm ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Crafting sweet memories, one cake at a time. Fresh ingredients, modern designs, and a whole lot of love.
            </p>
          </div>

          {/* Navigation Links */}
          <div>
            <h4
              className={`font-heading font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-charcoal'
              }`}
            >
              Navigation
            </h4>
            <nav className="flex flex-col gap-2">
              <Link
                to="/"
                className={`text-sm transition-colors duration-200 ${
                  isDark
                    ? 'text-gray-300 hover:text-coral-400'
                    : 'text-gray-600 hover:text-coral-500'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`text-sm transition-colors duration-200 ${
                  isDark
                    ? 'text-gray-300 hover:text-coral-400'
                    : 'text-gray-600 hover:text-coral-500'
                }`}
              >
                About Us
              </Link>
              <Link
                to="/gallery"
                className={`text-sm transition-colors duration-200 ${
                  isDark
                    ? 'text-gray-300 hover:text-coral-400'
                    : 'text-gray-600 hover:text-coral-500'
                }`}
              >
                Gallery
              </Link>
              <Link
                to="/menu"
                className={`text-sm transition-colors duration-200 ${
                  isDark
                    ? 'text-gray-300 hover:text-coral-400'
                    : 'text-gray-600 hover:text-coral-500'
                }`}
              >
                Menu
              </Link>
              <Link
                to="/contact"
                className={`text-sm transition-colors duration-200 ${
                  isDark
                    ? 'text-gray-300 hover:text-coral-400'
                    : 'text-gray-600 hover:text-coral-500'
                }`}
              >
                Contact
              </Link>
              <Link
                to="/cake-builder"
                className={`text-sm transition-colors duration-200 ${
                  isDark
                    ? 'text-gray-300 hover:text-coral-400'
                    : 'text-gray-600 hover:text-coral-500'
                }`}
              >
                Order Now
              </Link>
              <Link
                to="/taart-bestellen-hoofddorp"
                lang="nl"
                className={`text-sm transition-colors duration-200 ${
                  isDark
                    ? 'text-gray-300 hover:text-coral-400'
                    : 'text-gray-600 hover:text-coral-500'
                }`}
              >
                Taart bestellen (NL)
              </Link>
              <Link
                to="/privacy"
                className={`text-sm transition-colors duration-200 ${
                  isDark
                    ? 'text-gray-300 hover:text-coral-400'
                    : 'text-gray-600 hover:text-coral-500'
                }`}
              >
                Privacy Policy
              </Link>
            </nav>
          </div>

          {/* Social Media */}
          <div>
            <h4
              className={`font-heading font-semibold mb-4 ${
                isDark ? 'text-white' : 'text-charcoal'
              }`}
            >
              Connect
            </h4>
            <div className="flex gap-3">
              {/* Instagram */}
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isDark
                    ? 'bg-white/10 hover:bg-coral-500 text-white'
                    : 'bg-gray-200 hover:bg-coral-500 text-gray-700 hover:text-white'
                } transform hover:scale-110`}
                aria-label="Instagram"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>

              {/* Facebook */}
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isDark
                    ? 'bg-white/10 hover:bg-coral-500 text-white'
                    : 'bg-gray-200 hover:bg-coral-500 text-gray-700 hover:text-white'
                } transform hover:scale-110`}
                aria-label="Facebook"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>

              {/* Pinterest/Email */}
              <a
                href="mailto:hello@bakenook.com"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isDark
                    ? 'bg-white/10 hover:bg-coral-500 text-white'
                    : 'bg-gray-200 hover:bg-coral-500 text-gray-700 hover:text-white'
                } transform hover:scale-110`}
                aria-label="Email"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div
          className={`mt-12 pt-8 border-t ${
            isDark ? 'border-white/10' : 'border-gray-200'
          }`}
        >
          <p
            className={`text-sm text-center ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            © {new Date().getFullYear()} The BakeNook. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
